/*
 * pogi-kit-builder — homepage bundle bottom-sheet (§13.5 / §13.5a).
 * Opens when any [data-open-kit-builder] CTA (hero, sticky, etc.) is clicked
 * — document-level click delegation. Toggle products + Subscribe & save,
 * see a live subtotal / discounted total / free-shipping gap, and add every
 * selected variant to the cart in one POST /cart/add.js.
 *
 * Discount %, free-ship threshold, and money format come from data-* on the
 * host element (one source, set in the section schema — not hardcoded here).
 * Each product row carries its own data-variant-id / data-selling-plan-id /
 * data-price (cents). Rows with no real variant id are skipped on add.
 */
import { beginPogiCartUpdate, openPogiCart } from '@pogi/cart-contract';
import { addPogiCartLines } from '@pogi/cart-mutation';
import { formatShopifyMoney } from '@pogi/money';
import { renderSubscriptionUsage, subscriptionUsageForProduct } from '@pogi/buy-box-v2-subscription-config';

/* i18n: JA overrides come from the [data-kit-i18n] blob the section renders via
   `| t`. EVERY read falls back to the exact English literal that used to be
   hardcoded, so a missing key can never change the EN site. [[count]] placeholder
   (not {{ }}) so Liquid's t-filter never eats it. */
const kitTpl = (I, k, fb, vars) => {
  let s = (I && I[k]) || fb;
  if (vars) for (const key of Object.keys(vars)) s = s.split('[[' + key + ']]').join(String(vars[key]));
  return s;
};

const KIT_FOCUSABLE = [
  'a[href]',
  'area[href]',
  'summary',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class PogiKitBuilder extends HTMLElement {
  connectedCallback() {
    if (this._bound) return;
    this._bound = true;
    this.discount = (parseFloat(this.dataset.discount) || 0) / 100;
    this.bundleDiscount = parseFloat(this.dataset.bundleDiscount) || 0;
    this.freeShip = parseInt(this.dataset.freeship, 10) || 0; // cents
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    this.routesRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const i18nEl = this.querySelector('[data-kit-i18n]');
    this.I = {};
    if (i18nEl) { try { this.I = JSON.parse(i18nEl.textContent); } catch (e) { /* keep EN fallbacks */ } }
    this.t = (k, fb, vars) => kitTpl(this.I, k, fb, vars);
    this.sheet = this.querySelector('[data-kit-sheet]');
    this.products = Array.from(this.querySelectorAll('[data-kit-product]'));
    this.subToggle = this.querySelector('[data-kit-subscribe]');
    this.subscriptionRequired = Boolean(this.subscriptionRequired);
    this._opener = null;
    this._inerted = [];
    this._addInFlight = Boolean(this._addInFlight);
    this.defaultState ||= this.products.map((product) => ({
      product,
      selected: product.classList.contains('is-selected'),
      sellingPlanId: product.dataset.sellingPlanId || '',
    }));

    this._onHostClick = (e) => {
      const toggle = e.target.closest('[data-kit-product-toggle]');
      if (toggle) { this.toggle(toggle.closest('[data-kit-product]')); return; }
      if (e.target.closest('[data-kit-subscribe]')) { this.toggleSub(); return; }
      if (e.target.closest('[data-kit-add]')) { this.add(); return; }
      if (e.target.closest('[data-kit-close]')) { this.close(); return; }
      if (e.target === this) this.close(); // overlay click
    };
    this._onHostChange = (e) => {
      const select = e.target.closest('[data-kit-plan-select]');
      if (!select) return;
      const product = select.closest('[data-kit-product]');
      if (product) product.dataset.sellingPlanId = select.value;
      this.recompute();
    };
    this.addEventListener('click', this._onHostClick);
    this.addEventListener('change', this._onHostChange);

    // Global open trigger: any [data-open-kit-builder] CTA, anywhere on the page.
    this._onDocClick = (e) => {
      const trigger = e.target.closest('[data-open-kit-builder]');
      if (!trigger) return;
      e.preventDefault();
      this.applyTriggerSelection(trigger);
      this.open(trigger);
    };
    document.addEventListener('click', this._onDocClick);
    this._onKey = (e) => {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    };
    document.addEventListener('keydown', this._onKey, true);

    this.recompute();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onHostClick);
    this.removeEventListener('change', this._onHostChange);
    document.removeEventListener('click', this._onDocClick);
    document.removeEventListener('keydown', this._onKey, true);
    if (this._focusFrame) window.cancelAnimationFrame(this._focusFrame);
    this.restoreBackground();
    this._bound = false;
  }

  focusableElements() {
    if (!this.sheet) return [];
    return Array.from(this.sheet.querySelectorAll(KIT_FOCUSABLE)).filter((element) => (
      element.tabIndex >= 0
      && !element.hidden
      && !element.closest('[hidden],[aria-hidden="true"],[inert]')
      && element.getClientRects().length > 0
    ));
  }

  isolateBackground() {
    this.restoreBackground();
    let branch = this;
    while (branch && branch !== document.body) {
      const parent = branch.parentElement;
      if (!parent) break;
      for (const sibling of Array.from(parent.children)) {
        if (sibling === branch || sibling.hasAttribute('inert')) continue;
        sibling.setAttribute('inert', '');
        this._inerted.push(sibling);
      }
      branch = parent;
    }
  }

  restoreBackground() {
    for (const element of this._inerted || []) {
      element.removeAttribute('inert');
    }
    this._inerted = [];
  }

  focusDialog() {
    const target = this.sheet?.querySelector('[data-kit-close]')
      || this.focusableElements()[0]
      || this.sheet;
    if (!target) return;
    if (target === this.sheet && !target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  }

  trapFocus(event) {
    const focusable = this.focusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.focusDialog();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const index = focusable.indexOf(document.activeElement);
    if (index < 0 || (event.shiftKey && index === 0) || (!event.shiftKey && index === focusable.length - 1)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    }
  }

  money(cents) {
    return formatShopifyMoney(
      Number(cents) || 0,
      String(this.moneyFormat || '{{amount}}'),
      this.currency || 'USD',
    );
  }

  open(trigger = document.activeElement) {
    if (this.classList.contains('is-open')) return;
    this._opener = trigger && typeof trigger.focus === 'function' ? trigger : document.activeElement;
    this._prevOverflow = document.body.style.overflow;
    this._prevPaddingRight = document.body.style.paddingRight;
    // POGI: compensate the scrollbar width so locking scroll doesn't shift the page right.
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
    document.body.style.overflow = 'hidden';
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.isolateBackground();
    this._focusFrame = window.requestAnimationFrame(() => {
      this._focusFrame = null;
      if (this.classList.contains('is-open')) this.focusDialog();
    });
  }

  close({ restoreFocus = true } = {}) {
    if (!this.classList.contains('is-open')) return;
    if (this._focusFrame) {
      window.cancelAnimationFrame(this._focusFrame);
      this._focusFrame = null;
    }
    const opener = this._opener;
    this._opener = null;
    document.body.style.overflow = this._prevOverflow || '';
    document.body.style.paddingRight = this._prevPaddingRight || '';
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.restoreBackground();
    this.sheet?.dispatchEvent(new Event('close'));
    // A sticky trigger can legitimately change visibility while the modal is
    // open. Reconcile its own hidden/inert state after removing modal-owned
    // isolation, before deciding whether its opener is still a valid target.
    document.querySelectorAll('pogi-sticky-cta').forEach((sticky) => sticky._update?.());
    if (!restoreFocus) return;

    const openerVisible = (
      opener?.isConnected
      && typeof opener.focus === 'function'
      && !opener.closest?.('[hidden],[aria-hidden="true"],[inert]')
      && opener.getClientRects?.().length > 0
    );
    if (openerVisible) {
      opener.focus({ preventScroll: true });
      return;
    }

    // Never strand focus on a now-hidden opener or inside the closed sheet.
    const bodyHadTabindex = document.body.hasAttribute('tabindex');
    if (!bodyHadTabindex) document.body.setAttribute('tabindex', '-1');
    document.body.focus({ preventScroll: true });
    if (!bodyHadTabindex) document.body.removeAttribute('tabindex');
  }

  toggle(el) {
    if (!this.available(el)) return;
    const on = el.classList.toggle('is-selected');
    el.querySelector('[data-kit-product-toggle]')?.setAttribute('aria-pressed', String(on));
    this.recompute();
  }

  toggleSub() {
    if (!this.subToggle) return;
    const on = this.subToggle.classList.toggle('is-on');
    this.subToggle.setAttribute('aria-pressed', String(on));
    this.recompute();
  }

  selected() {
    return this.products.filter((p) => p.classList.contains('is-selected'));
  }

  available(product) {
    return product?.dataset.available === 'true';
  }

  subscribeOn() {
    return this.subToggle && this.subToggle.classList.contains('is-on');
  }

  distinctSelectedProductCount(selected = this.selected()) {
    return new Set(
      selected.map((product) => String(product.dataset.productId || '')).filter(Boolean),
    ).size;
  }

  bundleOffer() {
    return this.t('bundle_pending', '10% bundle savings apply in cart');
  }

  setSelected(product, selected) {
    selected = Boolean(selected && this.available(product));
    product.classList.toggle('is-selected', selected);
    product.querySelector('[data-kit-product-toggle]')?.setAttribute('aria-pressed', String(selected));
  }

  setPlan(product, sellingPlanId) {
    const select = product.querySelector('[data-kit-plan-select]');
    if (!select) {
      product.dataset.sellingPlanId = '';
      return false;
    }
    const requested = String(sellingPlanId || '');
    const option = Array.from(select.options).find((entry) => entry.value === requested);
    if (!option) {
      product.dataset.sellingPlanId = '';
      return false;
    }
    select.value = option.value;
    product.dataset.sellingPlanId = option.value;
    return true;
  }

  setSubscription(enabled) {
    if (!this.subToggle) return;
    this.subToggle.classList.toggle('is-on', enabled);
    this.subToggle.setAttribute('aria-pressed', String(enabled));
  }

  restoreDefaults() {
    for (const state of this.defaultState) {
      this.setSelected(state.product, state.selected);
      this.setPlan(state.product, state.sellingPlanId);
    }
  }

  applyTriggerSelection(trigger) {
    const variantId = String(trigger.dataset.kitVariantId || '');
    if (!variantId) {
      this.subscriptionRequired = false;
      this.restoreDefaults();
      if (trigger.dataset.kitMode === 'one-time') this.setSubscription(false);
      if (trigger.dataset.kitMode === 'subscription') this.setSubscription(true);
      this.recompute();
      return;
    }

    // An exact subscription-card CTA must never degrade to a generic kit or a
    // one-time add because the card and builder configuration drifted.
    this.subscriptionRequired = true;
    this.setSubscription(true);
    let matched = false;
    for (const product of this.products) {
      const selected = String(product.dataset.variantId || '') === variantId;
      this.setSelected(product, selected);
      if (selected) {
        matched = true;
        // Product cards promise a subscription for one exact pack/cadence.
        // Force subscription mode on and fail closed if that plan went stale.
        this.setPlan(product, trigger.dataset.kitSellingPlanId);
      }
    }

    // Leave every row unselected when the exact configured variant is absent.
    // Replacing it with merchant defaults would violate the card's promise.
    if (!matched) {
      for (const product of this.products) this.setSelected(product, false);
    }
    this.recompute();
  }

  priceFor(product, subscription) {
    const oneTime = parseInt(product.dataset.price, 10) || 0;
    if (!subscription) return oneTime;
    const planPrice = this.planPriceFor(product);
    return planPrice == null ? oneTime : planPrice;
  }

  planPriceFor(product) {
    const option = product.querySelector('[data-kit-plan-select]')?.selectedOptions?.[0];
    const planPrice = parseInt(option?.dataset.price, 10);
    return Number.isFinite(planPrice) ? planPrice : null;
  }

  syncPlanVisibility(subscription) {
    for (const product of this.products) {
      const plan = product.querySelector('[data-kit-plan-wrap]');
      const selected = subscription && product.classList.contains('is-selected');
      if (plan) plan.hidden = !selected;
      const example = product.querySelector('[data-kit-usage]');
      if (!example) continue;
      let options;
      try { options = JSON.parse(product.dataset.usageOptions || 'null'); } catch { options = null; }
      const usage = subscriptionUsageForProduct(product.dataset.usageHandle, options);
      const selectedPlan = product.querySelector('[data-kit-plan-select]')?.selectedOptions?.[0];
      renderSubscriptionUsage(example, {
        enabled: selected && this.available(product)
          && product.dataset.usageVariantId === product.dataset.variantId
          && Boolean(selectedPlan?.value) && selectedPlan.value === product.dataset.sellingPlanId
          && this.planPriceFor(product) != null,
        unitsPerPack: usage?.unitsPerPack, perDay: usage?.perDay,
        durationUnit: usage?.family === 'wipes' ? 'weeks' : 'days',
        template: this.I[`usage_${usage?.family}`], locale: this.dataset.displayLocale,
      });
    }
  }

  recompute() {
    const sel = this.selected();
    const sub = this.subscribeOn();
    const subtotal = sel.reduce((sum, product) => sum + this.priceFor(product, false), 0);
    const total = sel.reduce((sum, product) => sum + this.priceFor(product, sub), 0);
    const distinctProducts = this.distinctSelectedProductCount(sel);
    const bundleEligible = !sub && this.bundleDiscount === 10 && distinctProducts >= 2;
    const missingVariant = sel.some((product) => !parseInt(product.dataset.variantId, 10));
    const unavailable = sel.some((product) => !this.available(product));
    const missingPlan = sub && sel.some((product) => !product.dataset.sellingPlanId);
    const missingPlanPrice = sub && sel.some((product) => this.planPriceFor(product) == null);
    const missingSubscriptionControl = this.subscriptionRequired && !this.subToggle;
    const missingRequiredSubscription = this.subscriptionRequired && !sub;
    this.syncPlanVisibility(sub);

    const set = (sel2, val) => { const el = this.querySelector(sel2); if (el) el.textContent = val; };
    set('[data-kit-count]', this.t('count', sel.length + (sel.length === 1 ? ' item' : ' items'), { count: sel.length }));
    set('[data-kit-subtotal]', this.money(subtotal));
    set('[data-kit-total]', this.money(total));

    const strike = this.querySelector('[data-kit-strike]');
    if (strike) {
      const showStrike = sub && subtotal > total;
      const strikeStr = this.money(subtotal);
      strike.textContent = showStrike ? strikeStr : '';
      strike.style.display = showStrike ? '' : 'none';
    }

    const bundle = this.querySelector('[data-kit-bundle]');
    if (bundle) {
      const showBundleState = !sub && distinctProducts > 0;
      bundle.hidden = !showBundleState;
      bundle.dataset.state = bundleEligible ? 'eligible' : 'actionable';
      const bundleText = !showBundleState
        ? ''
        : bundleEligible
          ? this.bundleOffer()
          : this.t('bundle_action', 'Add a different one-time product for 10% off both');
      if (bundle.textContent !== bundleText) bundle.textContent = bundleText;
    }

    // Free shipping is universal now — every kit ships free, no threshold.
    const ship = this.querySelector('[data-kit-ship]');
    if (ship) {
      ship.textContent = this.t('free_ship', '✓ Free shipping on every order');
      ship.dataset.state = 'ok';
    }

    const add = this.querySelector('[data-kit-add]');
    if (add) {
      if (this._addInFlight) {
        add.disabled = true;
        add.textContent = this.t('adding', 'Adding…');
      } else if (
        sel.length === 0
        || missingVariant
        || unavailable
        || missingPlan
        || missingPlanPrice
        || missingSubscriptionControl
        || missingRequiredSubscription
      ) {
        add.disabled = true;
        add.textContent = (
          missingVariant
          || unavailable
          || missingPlan
          || missingPlanPrice
          || missingSubscriptionControl
          || missingRequiredSubscription
        )
          ? this.t('try_again', 'Try again')
          : this.t('pick_one', 'Pick at least one product');
      } else {
        add.disabled = false;
        const addCount = this.t(
          'add_count',
          'Add ' + sel.length + (sel.length === 1 ? ' item' : ' items') + ' to cart',
          { count: sel.length },
        );
        add.textContent = addCount;
      }
    }
  }

  async add() {
    if (this._addInFlight) return;
    // Reject the whole selection if stock is unavailable; silently dropping a
    // row would submit a different kit from the one the shopper chose.
    if (this.selected().some((product) => !this.available(product))) return;
    const sub = this.subscribeOn();
    if (this.subscriptionRequired && !sub) return;
    if (sub && this.selected().some((product) => !product.dataset.sellingPlanId || this.planPriceFor(product) == null)) return;
    const items = this.selected().map((p) => {
      const item = { id: parseInt(p.dataset.variantId, 10), quantity: 1 };
      if (sub && p.dataset.sellingPlanId) item.selling_plan = parseInt(p.dataset.sellingPlanId, 10);
      return item;
    }).filter((i) => i.id);
    if (!items.length) return;

    this._addInFlight = true;
    const btn = this.querySelector('[data-kit-add]');
    if (btn) { btn.disabled = true; btn.textContent = this.t('adding', 'Adding…'); }
    const cartUpdate = beginPogiCartUpdate({
      target: this.sheet || this,
      action: 'add',
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      source: 'pogi-kit-builder',
      detail: {
        sellingPlanIds: items.map((item) => item.selling_plan || null),
      },
    });
    try {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      const autoOpenDrawer = drawer?.querySelector('cart-drawer-component[auto-open]');
      const drawerSectionId =
        drawer?.querySelector('cart-items-component[data-section-id]')?.dataset.sectionId ||
        'cart-drawer-section';
      const result = await addPogiCartLines({
        cartAddUrl: `${this.routesRoot}cart/add.js`,
        cartJsonUrl: `${this.routesRoot}cart.js`,
        items,
        requestBody: JSON.stringify({
          items,
          sections: [drawerSectionId],
          sections_url: `${window.location.pathname}${window.location.search}`,
        }),
        requestOptions: {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        },
      });
      this.close();
      const drawerSection = result.addPayload?.sections?.[drawerSectionId];
      const drawerSectionReady =
        typeof drawerSection === 'string' && drawerSection.trim() !== '';
      cartUpdate.resolve(result.cart, {
        recovered: result.recovered,
        commitState: result.commitState,
        sections: result.addPayload?.sections,
        suppressAutoOpen: Boolean(autoOpenDrawer && !drawerSectionReady),
        skipSectionRefresh: Boolean(autoOpenDrawer && !drawerSectionReady),
      });
      if (autoOpenDrawer && !drawerSectionReady) {
        window.location.assign(`${this.routesRoot}cart`);
        return;
      }
      if (!autoOpenDrawer) await openPogiCart(`${this.routesRoot}cart`);
    } catch (e) {
      cartUpdate.fail(e, e.currentCart, {
        didError: true,
        commitState: e.commitState,
      });
      if (e.retrySafe) {
        this._addInFlight = false;
        this.recompute();
        const currentAdd = this.querySelector('[data-kit-add]');
        if (currentAdd && !currentAdd.disabled) {
          currentAdd.textContent = this.t('try_again', 'Try again');
        }
      } else {
        this.close();
        window.location.assign(`${this.routesRoot}cart`);
      }
      return;
    }
    this._addInFlight = false;
    this.recompute();
  }
}

if (!customElements.get('pogi-kit-builder')) {
  customElements.define('pogi-kit-builder', PogiKitBuilder);
}
